import { Component, EventEmitter, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import * as _ from 'underscore';
import { IObject } from '../../../../type_definitions/datatypes';

type GetterFunction<T> = (params?:IRichTextRendererParams) => T | any;
export type RichTextClickFunction = (event:Event, onBusy:EventEmitter<boolean>, params?:IRichTextRendererParams) => void;

export enum RichTextType {
  LINK = 'link',
  TEXT = 'text'
}

export interface IRichTextGrid {
  iconClass?:string | GetterFunction<string>;
  iconPosition?:'left' | 'right';
  href?:string | GetterFunction<string>;
  label:string|number;
  labelClass?:string;
  target?:string;
  linkClass?:string;
  wrapperClass?:string;
  labelFormatter?:Function;
  title?:string|number;
  clickFn?:RichTextClickFunction;
  textClass?:string;
  prefixLabel?:string;
  prefixClass?:string;
}

export interface IRichTextRendererParams extends ICellRendererParams {
  getValues:GetterFunction<IRichTextGrid[]>;
  type:RichTextType;
  showTooltip?:boolean;
}

const SPINNER_ICON:string = 'fa-spinner fa-spin';

@Component({
  selector: 'ag-rich-text',
  template: `
    <div *ngFor="let text of textValues" class="rich-text-value" [ngClass]="text.wrapperClass">
      <ng-template #prefixTemplate>
        <span class="prefix" [ngClass]="text.prefixClass" [innerHTML]="text.prefixLabel" *ngIf="text.prefixLabel"></span>
      </ng-template>
      
      <ng-container *ngTemplateOutlet="text.iconPosition === 'right' ? iconPositionRight : iconPositionLeft"></ng-container>

      <ng-template #iconPositionLeft>
        <div *ngIf="params.type === '${RichTextType.LINK}'">
          <ng-container *ngTemplateOutlet="prefixTemplate"></ng-container>
          <a [href]="getUrl(text)" target="_blank" [ngClass]="text.labelClass" (click)="invokeClickFn($event, text)" [tooltip]="params.showTooltip ? (text.title || text.label) : null" placement="top" container="body">
            <i class="fa" [ngClass]="busy ? '${SPINNER_ICON}' : getIconClass(text)" *ngIf="text.iconClass"></i><span [innerHTML]="text.label"></span>
          </a>
        </div>

        <div class="rich-text-content" *ngIf="params.type !== '${RichTextType.LINK}'" [ngClass]="text.labelClass">
          <ng-container *ngTemplateOutlet="prefixTemplate"></ng-container>
          <i class="fa" [ngClass]="getIconClass(text)" *ngIf="text.iconClass"></i>&nbsp;<span [innerHTML]="text.label" [ngClass]="text.textClass" [tooltip]="params.showTooltip ? (text.title || text.label) : null" placement="top" container="body"></span>
        </div>
      </ng-template>

      <ng-template #iconPositionRight>
        <div *ngIf="params.type === '${RichTextType.LINK}'">
          <ng-container *ngTemplateOutlet="prefixTemplate"></ng-container>
          <a [href]="getUrl(text)" target="_blank" [ngClass]="text.labelClass" (click)="invokeClickFn($event, text)" [tooltip]="params.showTooltip ? (text.title || text.label) : null" placement="top" container="body">
            <span [innerHTML]="text.label"></span><i class="fa rich-text-icon-right" [ngClass]="busy ? '${SPINNER_ICON}' : getIconClass(text)" *ngIf="text.iconClass"></i>
          </a>
        </div>

        <div class="rich-text-content" *ngIf="params.type !== '${RichTextType.LINK}'" [ngClass]="text.labelClass">
          <ng-container *ngTemplateOutlet="prefixTemplate"></ng-container>
          <span [innerHTML]="text.label" [ngClass]="text.textClass" [tooltip]="params.showTooltip ? (text.title || text.label) : null" placement="top" container="body"></span>&nbsp;<i class="fa" [ngClass]="getIconClass(text)" *ngIf="text.iconClass"></i>
        </div>
      </ng-template>
    </div>
  `,
  styleUrls: [ './ag-rich-text-renderer.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class AgRichTextRendererComponent implements ICellRendererAngularComp {
  public params!:IRichTextRendererParams;
  public textValues: IRichTextGrid[];
  public busy:boolean;
  public onBusy:EventEmitter<boolean> = new EventEmitter<boolean>();

  public agInit(params:IRichTextRendererParams):void {
    this.params = params;
    this.textValues = this.getTextValues();
    this.onBusy.subscribe((busy:boolean) => this.busy = busy);
  }

  public refresh(params:IRichTextRendererParams):boolean {
    this.params = params;
    this.textValues = this.getTextValues();
    // As we have updated the params we return true to let AG Grid know we have handled the refresh.
    // So AG Grid will not recreate the cell renderer from scratch.
    return true;
  }

  public getUrl(text:IRichTextGrid):string {
    if (_.isFunction(text?.href)) {
      return text.href(this.params);
    } else {
      return text.href;
    }
  }

  public getIconClass(text:IRichTextGrid):IObject {
    if (_.isFunction(text.iconClass)) {
      return text.iconClass(this.params);
    } else {
      return <any>text.iconClass;
    }
  }

  public invokeClickFn(event:Event, text:IRichTextGrid):void {
   if (_.isFunction(text.clickFn)) {
     text.clickFn(event, this.onBusy, this.params);
   }
   return;
  }

  private getTextValues():IRichTextGrid[] {
    if (this.params.getValues) {
      const text:IRichTextGrid[] | IRichTextGrid = this.params.getValues(this.params);

      if (_.isArray(text)) {
        return text;
      } else {
        return [ text ];
      }
    }

    return [];
  }
}